import { Box, Button, CircularProgress } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";

const minVideoHeightSmall = "320px";
const minVideoHeightSmaller = "240px";
const minVideoHeightSmallest = "200px";

export const Container = styled(Box)(({ theme }) => ({
  position: "relative",
  height: "100%",
  width: "100%",
  backgroundColor: "black",
  display: "flex",
  flexFlow: "column",
  zIndex: 0,
  overflow: "hidden",
  contain: "layout size", // Prevent layout shifts

  [theme.breakpoints.down("md")]: {
    contain: "none", // Allow layout shifts
  },

  // In fullscreen mode, change layout to allow absolute positioning
  "&.fullscreen": {
    position: "relative",
  },
}));

export const Spinner = styled(CircularProgress)(() => ({
  marginTop: "10em",
  marginBottom: "10em",
  position: "absolute",
  zIndex: 1,
}));

export const PlayerContainer = styled("div")<{ hasError?: boolean }>(({ theme, hasError }) => ({
  position: "relative",
  width: "100%",
  flex: "1 1 100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "0", // Allow container to shrink
  backgroundColor: "black", // Ensure video area is never empty before video element paints
  maxHeight: "calc(100vh - 196px)", // Prevent excessive height
  [theme.breakpoints.down("md")]: {
    minHeight: hasError ? minVideoHeightSmall : "0",
  },

  "&.fullscreen": {
    maxHeight: "none",
    minHeight: "0",
    flex: "1 1 auto",
  },

  "& #player-container": {
    position: "relative",
    paddingTop: 0,
    width: "100% !important",
    height: "auto !important",
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },

  "& #player-container video": {
    width: "100% !important",
    height: "auto !important",
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    position: "relative",
    top: "auto",
    left: "auto",
    transform: "none",

    [theme.breakpoints.down("md")]: {
      position: "relative",
      transform: "none",
      left: "auto",
      top: "auto",
    },
  },

  "&.fullscreen #player-container": {
    ["@media all and (orientation:landscape)"]: {
      paddingTop: "0",
    },
  },

  "&.fullscreen #player-container video": {
    width: "100% !important",

    ["@media all and (orientation:portrait)"]: {
      height: "auto !important",
      top: "50%",
      left: "0",
      transform: "translate(0, -50%)",
    },
  },
}));

const staticFlicker = keyframes`
  0% {
    transform: translate3d(0, 0, 0);
  }
  10% {
    transform: translate3d(-7%, 5%, 0);
  }
  20% {
    transform: translate3d(6%, -6%, 0);
  }
  30% {
    transform: translate3d(-4%, -3%, 0);
  }
  40% {
    transform: translate3d(8%, 4%, 0);
  }
  50% {
    transform: translate3d(-6%, 2%, 0);
  }
  60% {
    transform: translate3d(4%, -7%, 0);
  }
  70% {
    transform: translate3d(-8%, -5%, 0);
  }
  80% {
    transform: translate3d(5%, 6%, 0);
  }
  90% {
    transform: translate3d(-3%, 1%, 0);
  }
  100% {
    transform: translate3d(0, 0, 0);
  }
`;

export const StaticOverlay = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: "100px",
  opacity: "0.18",
  pointerEvents: "none",
  zIndex: 1,
  overflow: "hidden",
  "&::before": {
    content: "''",
    position: "absolute",
    inset: "-200%",
    backgroundImage: "url('/assets/white-noise.png')",
    backgroundRepeat: "repeat",
    backgroundSize: "auto",
    willChange: "transform",
    animation: `${staticFlicker} 0.55s steps(1, end) infinite`,
    opacity: 0.8,
  },
  "&::after": {
    content: "''",
    position: "absolute",
    inset: "-220%",
    backgroundImage: "url('/assets/white-noise.png')",
    backgroundRepeat: "repeat",
    backgroundSize: "auto",
    willChange: "transform",
    animation: `${staticFlicker} 0.67s steps(1, end) infinite`,
    opacity: 0.5,
  },
  [theme.breakpoints.down("lg")]: {
    minHeight: minVideoHeightSmall,
    bottom: "85px",
  },
  [theme.breakpoints.down("md")]: {
    minHeight: minVideoHeightSmaller,
    bottom: "80px",
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: minVideoHeightSmallest,
  },
}));

export const StaticMessage = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: "100px",
  zIndex: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  color: "#ffffff",
  padding: "16px",
  pointerEvents: "none",
  [theme.breakpoints.down("md")]: {
    minHeight: minVideoHeightSmaller,
    bottom: "80px",
  },
  [theme.breakpoints.down("sm")]: {
    minHeight: minVideoHeightSmallest,
  },
}));

export const StaticMessageContent = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  alignItems: "center",
  maxWidth: "320px",
  pointerEvents: "none",
}));

export const StaticTitle = styled("div")(() => ({
  fontSize: "48px",
  fontWeight: 700,
}));

export const StaticText = styled("div")(() => ({
  fontSize: "14px",
  lineHeight: 1,
  marginBottom: "10px",
}));

export const RetryButton = styled(Button)(() => ({
  marginTop: "8px",
  backgroundColor: "#1976d2",
  color: "#ffffff",
  textTransform: "none",
  pointerEvents: "auto",
  "&:hover": {
    backgroundColor: "#1565c0",
  },
}));

export const CaptionsContainer = styled("div")(() => ({
  flex: "0 0 auto",
}));
